import { useState } from 'react';
import type { OnChangeFn, PaginationState as TablePaginationState } from '@tanstack/react-table';
import {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  type UsePaginationReturn,
} from '@/shared/types/pagination';
import { computeVisiblePages } from '@/shared/utils/paginationUtils';

interface UseServerPaginationOptions {
  totalItems: number;
  initialPageSize?: number;
  initialPageIndex?: number;
}

/** Backend params — page is 1-based (server convention) */
export interface ServerPaginationApiParams {
  page: number;
  pageSize: number;
}

export interface UseServerPaginationReturn extends UsePaginationReturn {
  /** Ready to pass as `state.pagination` to useReactTable */
  paginationState: TablePaginationState;
  /** Ready to pass as `onPaginationChange` to useReactTable */
  onPaginationChange: OnChangeFn<TablePaginationState>;
  /** Converted params to send directly to the API (1-based page) */
  apiParams: ServerPaginationApiParams;
}

export function useServerPagination({
  totalItems,
  initialPageSize = DEFAULT_PAGE_SIZE,
  initialPageIndex = 0,
}: UseServerPaginationOptions): UseServerPaginationReturn {
  const [pageIndex, setPageIndex] = useState(initialPageIndex);
  const [pageSize, setPageSize] = useState(
    PAGE_SIZE_OPTIONS.includes(initialPageSize) ? initialPageSize : DEFAULT_PAGE_SIZE,
  );

  const totalPages = totalItems > 0 ? Math.ceil(totalItems / pageSize) : 0;

  const canPrevious = pageIndex > 0;
  const canNext = totalPages > 0 && pageIndex < totalPages - 1;

  const visiblePages = computeVisiblePages(pageIndex, totalPages);

  const paginationState: TablePaginationState = { pageIndex, pageSize };

  const onPaginationChange: OnChangeFn<TablePaginationState> = (updater) => {
    const next = typeof updater === 'function' ? updater({ pageIndex, pageSize }) : updater;
    setPageIndex(next.pageIndex);
    if (next.pageSize !== pageSize) {
      setPageSize(next.pageSize);
      setPageIndex(0);
    }
  };

  const apiParams: ServerPaginationApiParams = {
    page: pageIndex + 1, // 0-based internal → 1-based for backend
    pageSize,
  };

  const goToPage = (page: number) => {
    if (totalPages === 0) return;
    setPageIndex(Math.max(0, Math.min(page, totalPages - 1)));
  };

  const next = () => {
    setPageIndex((prev) => (totalPages > 0 ? Math.min(prev + 1, totalPages - 1) : prev));
  };

  const previous = () => {
    setPageIndex((prev) => Math.max(prev - 1, 0));
  };

  const first = () => setPageIndex(0);

  const last = () => {
    if (totalPages > 0) setPageIndex(totalPages - 1);
  };

  const changePageSize = (size: number) => {
    setPageSize(size);
    setPageIndex(0);
  };

  return {
    pageIndex,
    pageSize,
    totalItems,
    totalPages,
    visiblePages,
    canPrevious,
    canNext,
    goToPage,
    next,
    previous,
    first,
    last,
    changePageSize,
    paginationState,
    onPaginationChange,
    apiParams,
  };
}
